import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
} from 'react'

type AudioContextType = {
  audioRef: React.MutableRefObject<HTMLAudioElement | null>
  isPlaying: boolean
  volume: number
  playAudio: () => void
  pauseAudio: () => void
  toggleAudio: () => void
  setVolumeValue: (value: number) => void
}

const initialState: AudioContextType = {
  audioRef: { current: null },
  isPlaying: false,
  volume: 1,
  playAudio: () => {},
  pauseAudio: () => {},
  toggleAudio: () => {},
  setVolumeValue: () => {},
}

const AudioContext = createContext(initialState)

export function AudioProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const audioRef = useRef(new Audio('/audio/bg-music.mp3'))
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolume] = useState(1)

  // 設定音樂循環播放
  useEffect(() => {
    audioRef.current.loop = true
    audioRef.current.volume = volume
  }, [volume])

  const playAudio = () => {
    audioRef.current.play()
    setIsPlaying(true)
  }

  const pauseAudio = () => {
    audioRef.current.pause()
    setIsPlaying(false)
  }

  const toggleAudio = () => {
    if (isPlaying) {
      pauseAudio()
    } else {
      playAudio()
    }
  }

  const setVolumeValue = (value: number) => {
    const newVolume = Math.max(0, Math.min(value, 1))
    setVolume(newVolume)
    audioRef.current.volume = newVolume
  }

  return (
    <AudioContext.Provider
      value={{
        audioRef,
        isPlaying,
        volume,
        playAudio,
        pauseAudio,
        toggleAudio,
        setVolumeValue,
      }}
    >
      {children}
    </AudioContext.Provider>
  )
}

// 自訂 Hook，方便其他組件使用
export const useAudio = () => useContext(AudioContext)
